import { Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';
import AdminTenantsPage from './AdminTenantsPage';
import AdminEntitiesPage from './AdminEntitiesPage';
import AdminFormConfigsPage from './AdminFormConfigsPage';
import AdminDesignUploadsPage from './AdminDesignUploadsPage';
import AdminSubmissionsPage from './AdminSubmissionsPage';
import AdminCanvasPage from './AdminCanvasPage';
import { STUDIO_LAYOUT, STUDIO_TYPO } from './studioDesign';

type AdminTab = 'overview' | 'tenants' | 'entities' | 'configs' | 'uploads' | 'submissions' | 'canvas';

function tabFromPath(pathname: string): AdminTab {
  if (pathname.startsWith('/admin/tenants')) return 'tenants';
  if (pathname.startsWith('/admin/entities')) return 'entities';
  if (pathname.startsWith('/admin/configs')) return 'configs';
  if (pathname.startsWith('/admin/uploads')) return 'uploads';
  if (pathname.startsWith('/admin/submissions')) return 'submissions';
  if (pathname.startsWith('/admin/canvas')) return 'canvas';
  return 'overview';
}

function setPath(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function AdminShell() {
  const studioRedesignEnabled = String(import.meta.env.VITE_STUDIO_REDESIGN_V2 ?? 'true').toLowerCase() !== 'false';
  const [pathname, setPathname] = useState(window.location.pathname);
  const { activeTenantId, activeEntityId, activeConfigId, refresh } = useBusiness();
  const [activeConfigStatus, setActiveConfigStatus] = useState<string>('');
  const [submissionDone, setSubmissionDone] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);

  useEffect(() => {
    const handler = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const tab = tabFromPath(pathname);

  useEffect(() => {
    try {
      setUploadDone(localStorage.getItem('admin_phase1_upload_done') === '1');
      setSubmissionDone(localStorage.getItem('admin_phase1_submission_done') === '1');
    } catch {
      setUploadDone(false);
      setSubmissionDone(false);
    }
  }, [pathname]);

  useEffect(() => {
    const inferUploadDone = async () => {
      if (!activeTenantId || uploadDone) return;
      const resp = await apiFetch(`/api/design-uploads?tenant_id=${encodeURIComponent(activeTenantId)}`, { method: 'GET' });
      if (!resp.ok) return;
      const rows = (await resp.json()) as Array<{ upload_id: string }>;
      if (Array.isArray(rows) && rows.length > 0) setUploadDone(true);
    };
    void inferUploadDone();
  }, [activeTenantId, uploadDone]);

  useEffect(() => {
    const loadActiveConfig = async () => {
      if (!activeConfigId) {
        setActiveConfigStatus('');
        return;
      }
      const resp = await apiFetch(`/api/form-configs/${activeConfigId}`);
      if (!resp.ok) return;
      const cfg = await resp.json() as { status?: string };
      setActiveConfigStatus((cfg.status || '').toLowerCase());
    };
    void loadActiveConfig();
  }, [activeConfigId, pathname]);

  const checklist = useMemo(
    () => [
      { key: 'tenant', label: 'Select tenant', done: Boolean(activeTenantId), path: '/admin/tenants' },
      { key: 'entity', label: 'Select entity', done: Boolean(activeEntityId), path: '/admin/entities' },
      { key: 'config', label: 'Pick config', done: Boolean(activeConfigId), path: '/admin/configs' },
      { key: 'upload', label: 'Create/upload design draft', done: uploadDone, path: '/admin/uploads' },
      { key: 'publish', label: 'Publish config', done: activeConfigStatus === 'published' || activeConfigStatus === 'archived', path: '/admin/configs' },
      { key: 'submission', label: 'Create test submission', done: submissionDone, path: '/admin/submissions' },
    ] as const,
    [activeTenantId, activeEntityId, activeConfigId, uploadDone, activeConfigStatus, submissionDone],
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        '@supports (height: 100dvh)': { minHeight: '100dvh' },
        bgcolor: STUDIO_LAYOUT.shellBg,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Paper sx={{ p: 2.5, mb: 2, borderRadius: STUDIO_LAYOUT.panelRadius, border: `1px solid ${STUDIO_LAYOUT.panelBorder}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: STUDIO_TYPO.titleColor }}>Admin Portal</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage business context, tenants, and entities.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => void refresh()}>
                Refresh Context
              </Button>
              <Button variant="outlined" onClick={() => setPath('/')}>
                Back To Clinical App
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v: AdminTab) => setPath(v === 'overview' ? '/admin' : `/admin/${v}`)}
            sx={{
              mt: 2,
              '& .MuiTabs-indicator': { bgcolor: STUDIO_TYPO.titleColor, height: 3 },
              '& .MuiTab-root.Mui-selected': { color: STUDIO_TYPO.titleColor, fontWeight: 700 },
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Tenants" value="tenants" />
            <Tab label="Entities" value="entities" />
            <Tab label="Configs" value="configs" />
            <Tab label="Uploads" value="uploads" />
            <Tab label="Submissions" value="submissions" />
            <Tab label="Canvas" value="canvas" />
          </Tabs>
        </Paper>

        {tab === 'overview' && (
          <Paper sx={{ p: 2.5, borderRadius: STUDIO_LAYOUT.panelRadius, border: `1px solid ${STUDIO_LAYOUT.panelBorder}` }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: STUDIO_TYPO.titleColor }} gutterBottom>
              Current Selection
            </Typography>
            <Typography variant="body2">Tenant: {activeTenantId || '(none)'}</Typography>
            <Typography variant="body2">Entity: {activeEntityId || '(none)'}</Typography>
            <Typography variant="body2">Config: {activeConfigId || '(none)'}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Chip size="small" label={activeTenantId ? 'Tenant selected' : 'Select tenant first'} color={activeTenantId ? 'success' : 'default'} />
              <Chip size="small" label={activeEntityId ? 'Entity selected' : 'Select entity next'} color={activeEntityId ? 'success' : 'default'} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Use Tenants → Entities to set context. For submissions, pick a form config on the Submissions tab (or Canvas).
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              End-user guide: docs/ADMIN_PORTAL_USER_GUIDE.md
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: STUDIO_TYPO.titleColor, mt: 2 }}>
              Guided Onboarding
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Follow this sequence for a successful first-time setup.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              {checklist.map((c, idx) => (
                <Button
                  key={c.key}
                  size="small"
                  variant={c.done ? 'contained' : 'outlined'}
                  color={c.done ? 'success' : 'primary'}
                  onClick={() => setPath(c.path)}
                >
                  {idx + 1}. {c.label}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
              <Chip
                size="small"
                color={checklist.every((c) => c.done) ? 'success' : 'default'}
                label={
                  checklist.every((c) => c.done)
                    ? 'Phase 1 onboarding ready'
                    : `${checklist.filter((c) => c.done).length}/${checklist.length} steps completed`
                }
              />
            </Stack>
          </Paper>
        )}
        {tab === 'tenants' && <AdminTenantsPage />}
        {tab === 'entities' && <AdminEntitiesPage />}
        {tab === 'configs' && <AdminFormConfigsPage />}
        {tab === 'uploads' && <AdminDesignUploadsPage />}
        {tab === 'submissions' && <AdminSubmissionsPage />}
        {tab === 'canvas' && studioRedesignEnabled && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
            <AdminCanvasPage />
          </Box>
        )}
        {tab === 'canvas' && !studioRedesignEnabled && (
          <Paper sx={{ p: 2.5, borderRadius: STUDIO_LAYOUT.panelRadius, border: `1px solid ${STUDIO_LAYOUT.panelBorder}` }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: STUDIO_TYPO.titleColor }} gutterBottom>
              Studio redesign is disabled
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enable <code>VITE_STUDIO_REDESIGN_V2=true</code> to access the modern Studio canvas.
            </Typography>
          </Paper>
        )}
      </Box>

      <Box className="app-footer" sx={{ py: 0.75, px: 2, height: STUDIO_LAYOUT.footerHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          Copyright © Dure Technologies Pvt. Ltd.
        </Typography>
      </Box>
    </Box>
  );
}

